'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoadedAvifSource } from '@/components/tools/avif-tool-shared';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  convertAvifToJpg,
  convertAvifToPng,
  convertAvifToWebp,
  formatFileSize,
} from '@/lib/browser-avif-tools';
import { getBaseName } from '@/lib/browser-image-tools';

type Variant = 'avif-to-jpg' | 'avif-to-png' | 'avif-to-webp';
type QualityPreset = 'high' | 'balanced' | 'smaller';

const presetQualityMap: Record<QualityPreset, number> = {
  high: 90,
  balanced: 82,
  smaller: 68,
};

const copy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    downloadLabel: string;
    downloadExtension: string;
    qualityEnabled: boolean;
    qualityNote: string;
  }
> = {
  'avif-to-jpg': {
    title: 'Convert AVIF to JPG for wider compatibility',
    description:
      'Decode one AVIF locally, preview it in the browser, and export a JPG that is easier to upload or hand off.',
    badge: 'JPG',
    actionLabel: 'Convert to JPG',
    downloadLabel: 'Download JPG',
    downloadExtension: 'jpg',
    qualityEnabled: true,
    qualityNote: 'Transparency is flattened onto white for JPG export.',
  },
  'avif-to-png': {
    title: 'Convert AVIF to PNG and keep transparency when present',
    description:
      'Open one AVIF in a modern browser runtime and export a PNG that keeps alpha for transparent assets and design handoff.',
    badge: 'PNG',
    actionLabel: 'Convert to PNG',
    downloadLabel: 'Download PNG',
    downloadExtension: 'png',
    qualityEnabled: false,
    qualityNote: 'PNG export keeps transparency when the AVIF contains alpha.',
  },
  'avif-to-webp': {
    title: 'Convert AVIF to WebP for web delivery',
    description:
      'Open one AVIF, keep alpha when possible, and export a WebP locally for a more familiar web-ready format.',
    badge: 'WebP',
    actionLabel: 'Convert to WebP',
    downloadLabel: 'Download WebP',
    downloadExtension: 'webp',
    qualityEnabled: true,
    qualityNote: 'WebP export keeps transparency when the source contains alpha.',
  },
};

export default function BrowserAvifSourceConverter({ variant }: { variant: Variant }) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource, isSupported } = useLoadedAvifSource();
  const [qualityPreset, setQualityPreset] = useState<QualityPreset>('balanced');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  const toolCopy = copy[variant];
  const activeQuality = presetQualityMap[qualityPreset];

  const resultPreviewStyle = useMemo(
    () => (variant === 'avif-to-jpg' ? { background: '#ffffff' } : undefined),
    [variant],
  );

  useEffect(() => {
    return () => {
      if (resultUrl) {
        URL.revokeObjectURL(resultUrl);
      }
    };
  }, [resultUrl]);

  function resetOutputState() {
    if (resultUrl) {
      URL.revokeObjectURL(resultUrl);
    }

    setResultUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF file could not be loaded.',
      );
    }
  }

  async function runConversion() {
    if (!source) {
      setErrorMessage('Choose one AVIF first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another AVIF conversion right now.',
      );
      const blob =
        variant === 'avif-to-jpg'
          ? await convertAvifToJpg({ file: source.file, quality: activeQuality })
          : variant === 'avif-to-png'
            ? await convertAvifToPng(source.file)
            : await convertAvifToWebp({ file: source.file, quality: activeQuality });
      const nextUrl = URL.createObjectURL(blob);

      resetOutputState();
      setResultUrl(nextUrl);
      setDownloadName(`${getBaseName(source.file.name)}.${toolCopy.downloadExtension}`);
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess(toolCopy.actionLabel, credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The AVIF export could not be created.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>{toolCopy.title}</h2>
          <p>{toolCopy.description}</p>
        </div>
        <span className="tool-live-badge">{toolCopy.badge}</span>
      </div>

      {!isSupported ? (
        <p className="support-feedback support-feedback-error">
          This browser cannot process AVIF locally yet. Use a modern Chromium or Safari build with
          AVIF runtime support.
        </p>
      ) : null}

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            disabled={!isSupported}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one AVIF or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.metadata.width} × ${source.metadata.height}`
                  : 'Still AVIF only in modern browsers'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/avif,.avif"
            className="tool-live-file-input"
            disabled={!isSupported}
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          {toolCopy.qualityEnabled ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Quality preset</span>
                <select
                  onChange={(event) => {
                    setQualityPreset(event.target.value as QualityPreset);
                    resetOutputState();
                  }}
                  value={qualityPreset}
                >
                  <option value="high">Higher fidelity</option>
                  <option value="balanced">Balanced</option>
                  <option value="smaller">Smaller file</option>
                </select>
              </label>
            </div>
          ) : null}

          <div className="tool-live-panel-note">
            <strong>Export note</strong>
            <span>{toolCopy.qualityNote}</span>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing || !isSupported}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? 'Converting…' : toolCopy.actionLabel}
            </button>
            {resultUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={resultUrl}>
                {toolCopy.downloadLabel}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Source summary</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Dimensions</strong>
              <span>
                {source ? `${source.metadata.width} × ${source.metadata.height}` : 'No AVIF loaded'}
              </span>
            </div>
            <div>
              <strong>Transparency</strong>
              <span>
                {source
                  ? source.metadata.hasAlpha
                    ? 'Detected in source AVIF'
                    : 'No meaningful alpha detected'
                  : 'Not checked yet'}
              </span>
            </div>
            {toolCopy.qualityEnabled ? (
              <div>
                <strong>Preset</strong>
                <span>{qualityPreset}</span>
              </div>
            ) : null}
            <div>
              <strong>Result size</strong>
              <span>{resultSize ? formatFileSize(resultSize) : 'Not exported yet'}</span>
            </div>
          </div>
        </aside>
      </div>

      <div className="tool-live-preview">
        <h3>Preview</h3>
        <div className="tool-image-compare-grid">
          <div className="tool-image-compare-card">
            <strong>Source AVIF</strong>
            <div className="tool-live-preview-frame">
              {source ? (
                <img alt="Source AVIF preview" src={source.previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Source preview</strong>
                  <span>Load one AVIF to inspect it before export.</span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-image-compare-card">
            <strong>{toolCopy.badge} export</strong>
            <div className="tool-live-preview-frame" style={resultPreviewStyle}>
              {resultUrl ? (
                <img alt={`${toolCopy.badge} result preview`} src={resultUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>No export yet</strong>
                  <span>Run the conversion to preview the result.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
